/**
 * Receipt detail sticky header — mirrors the invoice/bill `.composer-head`.
 *
 * Title is "Receipt · RCP-<id8>" + a status pill (Recorded / Void). The
 * subtitle stamps the customer + received date + cleared invoice. Action
 * cluster holds Print + Download PDF + (founder-only) Void.
 */
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Download, FileText, Printer, Trash2 } from "lucide-react";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";

export interface ReceiptDetailHeaderProps {
  receipt: {
    id: string;
    receivedOn: string;
    amountPaise: string;
    voidsId: string | null;
  };
  invoice: { docNumber: string | null; id: string } | null;
  customer: { name: string };
  isFounder: boolean;
  /** True when this (original) receipt has been voided by a reversing row. */
  isVoided?: boolean;
  onPrint: () => void;
  onDownloadPdf: () => void;
  onVoid: () => void;
  pendingVoid?: boolean;
}

export function ReceiptDetailHeader({
  receipt,
  invoice,
  customer,
  isFounder,
  isVoided,
  onPrint,
  onDownloadPdf,
  onVoid,
  pendingVoid,
}: ReceiptDetailHeaderProps) {
  // A row is "void" if it is itself a reversal, or if a reversal voids it.
  const isVoid = !!receipt.voidsId || !!isVoided;
  const rcpLabel = `RCP-${receipt.id.slice(0, 8)}`;
  return (
    <header className="composer-head">
      <Link to="/sales/receipts" className="composer-head-back" aria-label="Back to receipts">
        <ArrowLeft size={16} strokeWidth={2.2} />
      </Link>
      <div className="composer-head-text">
        <h1>
          <span>Receipt</span>
          <span className="doc-num num">{rcpLabel}</span>
          <span className={`status-pill ${isVoid ? "void" : "paid"}`}>
            {isVoid ? "Void" : "Recorded"}
          </span>
        </h1>
        <p className="sub">
          <strong>{customer.name}</strong>
          <span className="sep">·</span>
          <span>Received</span> <span className="num">{formatDate(receipt.receivedOn)}</span>
          <span className="sep">·</span>
          <span className="num">{paiseToInrShort(BigInt(receipt.amountPaise))}</span>
          {invoice ? (
            <>
              <span className="sep">·</span>
              <Link
                to="/sales/invoices/$invoiceId"
                params={{ invoiceId: invoice.id }}
                className="num"
                style={{ color: "var(--sky-700, #0369a1)", textDecoration: "underline" }}
              >
                {invoice.docNumber ?? "Draft invoice"}
              </Link>
            </>
          ) : null}
        </p>
      </div>
      <div className="composer-head-actions">
        <button
          type="button"
          className="icon-action"
          onClick={onPrint}
          title="Print"
          aria-label="Print"
        >
          <Printer size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          className="icon-action"
          onClick={onDownloadPdf}
          title="Download PDF"
          aria-label="Download PDF"
        >
          <Download size={14} strokeWidth={2} />
        </button>
        <button
          type="button"
          className="icon-action"
          title="Print preview"
          aria-label="Print preview"
        >
          <FileText size={14} strokeWidth={2} />
        </button>
        {isFounder && !isVoid ? (
          <button
            type="button"
            onClick={onVoid}
            disabled={pendingVoid}
            className="icon-action"
            title="Void receipt"
            aria-label="Void receipt"
            style={{ color: "var(--rose-700, #be185d)" }}
          >
            <Trash2 size={14} strokeWidth={2} />
          </button>
        ) : null}
      </div>
    </header>
  );
}
